import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/AuthLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";

const COMMON_STARTS = [
  { name: "Vodka, 40%", quantityValue: 700, quantityUnit: "ml" },
  { name: "Cane sugar", quantityValue: 1, quantityUnit: "kg" },
  { name: "Citrus peel", quantityValue: 1, quantityUnit: "each" },
  { name: "Green cardamom", quantityValue: 35, quantityUnit: "g" },
];

interface Row {
  name: string;
  quantityValue: number;
  quantityUnit: string;
}

export function OnboardingShelf() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [draftName, setDraftName] = useState("");
  const [busy, setBusy] = useState(false);

  function addRow(row: Row) {
    if (rows.some((r) => r.name.toLowerCase() === row.name.toLowerCase())) return;
    setRows((r) => [...r, row]);
  }

  function addFromInput() {
    if (!draftName.trim()) return;
    addRow({ name: draftName.trim(), quantityValue: 1, quantityUnit: "each" });
    setDraftName("");
  }

  async function submit(skip = false) {
    setBusy(true);
    try {
      await api.patch("/me/shelf", { items: skip ? [] : rows });
      await refresh();
      navigate("/onboarding/safety");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      posterTitle="What's already in the cupboard?"
      posterBody="Four items is enough to start. Alembic works out what you can make from them."
      step={3}
    >
      <span className="eyebrow">Step 3 of 4 · four items is plenty</span>
      <h2 style={{ margin: "12px 0 0", fontSize: 38, lineHeight: 1.05, letterSpacing: "-0.028em", marginLeft: "-0.055em" }}>
        Seed your shelf
      </h2>
      <p className="muted" style={{ margin: "14px 0 24px", fontSize: 14.5, lineHeight: 1.6 }}>
        Tell us roughly what you have. You can add the rest later, or never.
      </p>

      <div className="flex-row" style={{ marginBottom: 14 }}>
        <input
          className="input"
          style={{ minHeight: 46 }}
          placeholder="Type an ingredient — gentian, sloes, 96% spirit…"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFromInput())}
        />
        <button type="button" className="btn btn-secondary" onClick={addFromInput}>
          Add
        </button>
      </div>

      <div style={{ borderBottom: "2px solid var(--color-divider)", marginBottom: 2 }}>
        {rows.map((row, i) => (
          <div
            key={row.name}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) 110px 34px",
              gap: 12,
              alignItems: "center",
              padding: "10px 0",
              borderBottom: "1px solid var(--color-divider)",
            }}
          >
            <span style={{ fontSize: 14.5 }}>{row.name}</span>
            <input
              className="input tabular"
              value={`${row.quantityValue} ${row.quantityUnit}`}
              onChange={(e) => {
                const [val, unit] = e.target.value.split(" ");
                setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, quantityValue: Number(val) || 0, quantityUnit: unit || r.quantityUnit } : r)));
              }}
            />
            <button
              type="button"
              className="btn"
              style={{ justifyContent: "center", color: "color-mix(in srgb, var(--color-text) 45%, transparent)" }}
              onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <p className="muted-strong" style={{ margin: "14px 0 0", fontSize: 11, letterSpacing: "0.09em", textTransform: "uppercase" }}>
        Common starts, if it's easier
      </p>
      <div className="wrap-gap" style={{ margin: "10px 0 26px" }}>
        {COMMON_STARTS.map((s) => (
          <button
            key={s.name}
            type="button"
            className="tag tag-outline"
            style={{ padding: "8px 14px", fontSize: 13, border: "1px solid var(--color-accent)", background: "none", cursor: "pointer" }}
            onClick={() => addRow(s)}
          >
            + {s.name}
          </button>
        ))}
      </div>

      <div className="flex-row">
        <button className="btn btn-primary" style={{ minHeight: 46 }} disabled={busy} onClick={() => submit(false)}>
          Continue
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={() => submit(true)}>
          I'll do this later
        </button>
      </div>
    </AuthLayout>
  );
}
